import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { COUNTRY_TO_LANGUAGE, EU_LANGUAGES, LanguageCode, VAT_RATES } from '@/lib/languages';

interface LocationData {
  country: string;
  language: LanguageCode;
  vatRate: number;
  timezone: string;
  isLoading: boolean;
}

export function useLocationDetection() {
  const { i18n } = useTranslation();
  const [locationData, setLocationData] = useState<LocationData>({
    country: 'IT',
    language: 'it',
    vatRate: 22,
    timezone: 'Europe/Rome',
    isLoading: true,
  });

  useEffect(() => {
    const detectLocation = async () => {
      // Check if user has EXPLICITLY selected a language (manual selection)
      // We use a separate flag to distinguish manual selection from auto-detection
      const manuallySelected = localStorage.getItem('languageManuallySelected') === 'true';
      const storedLang = localStorage.getItem('i18nextLng');

      if (manuallySelected && storedLang && storedLang !== 'undefined') {
        // User explicitly selected a language, respect it but still detect timezone/country
        const storedTimezone = localStorage.getItem('detectedTimezone');
        const storedCountry = localStorage.getItem('detectedCountry') || 'IT';

        try {
          const { data, error } = await supabase.functions.invoke('detect-location');
          if (!error && data) {
            const country = (data.country || storedCountry) as string;
            const timezone = (data.timezone || storedTimezone || 'Europe/Rome') as string;

            localStorage.setItem('detectedCountry', country);
            localStorage.setItem('detectedTimezone', timezone);

            setLocationData({
              country,
              language: storedLang as LanguageCode,
              vatRate: VAT_RATES[country] || VAT_RATES[storedCountry] || 22,
              timezone,
              isLoading: false,
            });
            return;
          }
        } catch (e) {
          // fall back to stored defaults
        }

        const vatRate = VAT_RATES[storedCountry] || 22;
        setLocationData({
          country: storedCountry,
          language: storedLang as LanguageCode,
          vatRate,
          timezone: storedTimezone || 'Europe/Rome',
          isLoading: false,
        });
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('detect-location');
        
        if (!error && data) {
          const country = data.country || 'IT';
          const language = (data.language || COUNTRY_TO_LANGUAGE[country] || 'en') as LanguageCode;
          const vatRate = VAT_RATES[country] || 22;
          const timezone = data.timezone || 'Europe/Rome';

          setLocationData({
            country,
            language,
            vatRate,
            timezone,
            isLoading: false,
          });

          // Set language based on IP detection (always, unless manually selected)
          // Default to Italian if detected language is not supported
          const supportedLangs = Object.keys(EU_LANGUAGES);
          const langToSet = supportedLangs.includes(language) ? language : 'it';
          i18n.changeLanguage(langToSet);

          // Store detected values
          localStorage.setItem('detectedCountry', country);
          localStorage.setItem('detectedTimezone', timezone);
          // Clear manual selection flag since we're using auto-detection
          localStorage.removeItem('languageManuallySelected');
        }
      } catch (error) {
        console.error('Location detection failed:', error);
      } finally {
        setLocationData(prev => ({ ...prev, isLoading: false }));
      }
    };

    detectLocation();
  }, [i18n]);

  return locationData;
}

// Helper function to parse various date formats
function parseDate(date: string | Date): Date | null {
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof date !== 'string' || !date) return null;

  // Try standard parsing first
  let dateObj = new Date(date);
  if (!isNaN(dateObj.getTime())) return dateObj;

  // Try parsing YYYYMMDD format (common in TED API)
  if (/^\d{8}$/.test(date)) {
    const year = date.slice(0, 4);
    const month = date.slice(4, 6);
    const day = date.slice(6, 8);
    dateObj = new Date(`${year}-${month}-${day}`);
    if (!isNaN(dateObj.getTime())) return dateObj;
  }

  // Try parsing YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    dateObj = new Date(date + 'T00:00:00');
    if (!isNaN(dateObj.getTime())) return dateObj;
  }

  // Try parsing TED format like YYYY-MM-DD+02:00 (missing "T")
  if (/^\d{4}-\d{2}-\d{2}[+-]\d{2}:\d{2}$/.test(date)) {
    const normalized = `${date.slice(0, 10)}T00:00:00${date.slice(10)}`;
    dateObj = new Date(normalized);
    if (!isNaN(dateObj.getTime())) return dateObj;
  }

  return null;
}

// Helper function to format dates according to user's locale and timezone
export function formatDateLocalized(
  date: string | Date | null | undefined,
  options?: {
    includeTime?: boolean;
    timezone?: string;
    locale?: string;
  }
): string {
  if (!date) return '-';
  
  const dateObj = parseDate(date);
  if (!dateObj) return '-';

  const timezone = options?.timezone || localStorage.getItem('detectedTimezone') || 'Europe/Rome';
  const storedLang = localStorage.getItem('i18nextLng') || 'it';
  const locale = options?.locale || storedLang;

  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  if (options?.includeTime) {
    formatOptions.hour = '2-digit';
    formatOptions.minute = '2-digit';
  }

  try {
    return new Intl.DateTimeFormat(locale, formatOptions).format(dateObj);
  } catch {
    // Fallback if timezone is invalid
    return new Intl.DateTimeFormat(locale, { ...formatOptions, timeZone: undefined }).format(dateObj);
  }
}

// Format relative time (e.g., "2 hours ago", "in 3 days")
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '-';

  const dateObj = parseDate(date as any);
  if (!dateObj) return '-';

  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));

  const storedLang = localStorage.getItem('i18nextLng') || 'it';

  try {
    const rtf = new Intl.RelativeTimeFormat(storedLang, { numeric: 'auto' });
    
    if (Math.abs(diffDays) >= 1) {
      return rtf.format(diffDays, 'day');
    } else if (Math.abs(diffHours) >= 1) {
      return rtf.format(diffHours, 'hour');
    } else {
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      return rtf.format(diffMinutes, 'minute');
    }
  } catch {
    return formatDateLocalized(date);
  }
}
